import React, { useEffect, useState } from 'react';
import { Button, Card, Modal, Space, Typography } from 'antd';
import { useGoogleLogin } from '@react-oauth/google';

import { REDIRECT_URI } from 'utils/envUtil';
import { showMessage, showNotification } from 'utils/notificationUtil';
import {
    getMyIntegrationCredentials,
    googleLogout,
    logoutByProvider,
    swapGoogleCodeWithTokenForIntegrationAccess,
} from 'utils/ez_api/integrationCredentialAPIs';
import {
    IntegrationCredentialType,
    IntegrationServiceTypeEnum,
    IntegrationTypeEnum,
} from 'types/integrationCredential';
import { syncAllIntegratedCalendars } from 'utils/ez_api/calendarAPIs';

type GoogleCalendarLoginButtonProps = {};

const { Title, Text } = Typography;

const DEFAULT_SCOPE = [
    'openid',
    'profile',
    'email',
    'https://www.googleapis.com/auth/calendar',
].join(' ');

export const GoogleCalendarLoginSection: React.FC<GoogleCalendarLoginButtonProps> = ({}) => {
    const [googleCredential, setGoogleCredential] = useState<IntegrationCredentialType | null>(
        null
    );

    const startGoogleAuth = useGoogleLogin({
        flow: 'auth-code',
        scope: DEFAULT_SCOPE,
        redirect_uri: REDIRECT_URI,
        include_granted_scopes: true,
        onSuccess: ({ code, scope: grantedScope }) => {
            console.log('🚀 ~ GoogleCalendarLoginButton ~ grantedScope:', grantedScope);
            if (!code) {
                showMessage('error', 'Failed to start Google OAuth flow.');
                return;
            }
            if (grantedScope && !grantedScope.includes('calendar')) {
                showNotification(
                    'warning',
                    'Calendar scope not granted. Please re-authorize and include calendar access.'
                );
            } else {
                swapGoogleCodeWithTokenForIntegrationAccess(
                    code,
                    IntegrationServiceTypeEnum.CALENDAR
                ).then(resp => {
                    console.log('🚀 ~ swapGoogleCodeWithTokenForIntegrationAccess ~ resp:', resp);
                    setGoogleCredential(resp.data as IntegrationCredentialType);

                    showNotification(
                        'success',
                        'You have connected your Google account to your account.'
                    );
                });
            }
        },
        onError: () => showMessage('error', 'Failed to start Google OAuth flow.'),
    });

    useEffect(() => {
        getMyIntegrationCredentials(IntegrationTypeEnum.GOOGLE).then(resp => {
            const credentials = resp.data as IntegrationCredentialType[];
            if (credentials.length > 0) {
                const activeGoogleCredential = credentials.find(
                    credential => credential.is_calendar_enabled && credential.email
                );
                setGoogleCredential(activeGoogleCredential ?? null);
            } else {
                setGoogleCredential(null);
            }
        });
    }, []);

    const _onGoogleLogout = () => {
        // googleLogout(googleCredential?.email ?? '').then(() => {
        //     setGoogleCredential(null);
        //     showNotification('success', 'You have unlinked a Google account successfully.');
        // });
        logoutByProvider(
            IntegrationTypeEnum.GOOGLE,
            googleCredential?.email ?? '',
            IntegrationServiceTypeEnum.CALENDAR
        ).then(() => {
            setGoogleCredential(null);
            showNotification('success', 'You have unlinked a Google account successfully.');
        });
    };

    return (
        <Card>
            <Space direction="vertical" size="small">
                <Title level={4} className="mb-0">
                    Google Calendar
                </Title>
                <Text type="secondary">Start Google OAuth to allow calendar access.</Text>

                <Button type="primary" onClick={() => startGoogleAuth()}>
                    {googleCredential && googleCredential.email
                        ? 'Connected as ' + googleCredential.email
                        : 'Connect Google Calendar'}
                </Button>

                {googleCredential && googleCredential.email && (
                    <Button
                        // type="link"
                        onClick={() => {
                            Modal.confirm({
                                title: 'Disconnect Google Calendar',
                                content: (
                                    <div>
                                        <p>
                                            Are you sure you want to disconnect your Google
                                            Calendar?
                                        </p>
                                        <p>
                                            If you use the same Google account for your linked
                                            email, you will need to re-authorize to connect again.
                                        </p>
                                    </div>
                                ),
                                onOk: () => {
                                    _onGoogleLogout();
                                },
                            });
                        }}
                    >
                        Disconnect
                    </Button>
                )}
            </Space>
        </Card>
    );
};

export default GoogleCalendarLoginSection;
